import {
    CameraBrandModel,
    CameraModel,
    ControlModel,
    ControlTypeModel,
    GlassControlParameters,
    InputModel,
    InputTypeModel,
    MediaModel,
    SensorTypeModel,
    UserModel,
} from '../../types/control';
import {BridgeDeviceModel} from '../../types/bridge-device';
import {IntercomModel} from '../../types/intercom';
import {DoorModel} from '../../types/door';
import {GatewayModel} from '../../types/gateway';
import {DoorBrandModel} from '../../types/door-brand';
import {BridgeDeviceTypeModel} from '../../types/bridge-device-type';
import {AlarmConfigModel} from '../../types/alarm-config';
import {LocationModel} from '../../types/location';
import {ConciergeModel} from '../../types/concierge';
import {ConciergeTypeModel} from '../../types/concierge-type';

export const initialControlState: ControlState = {
    controlTypeList: [],
    userList: [],
    controlList: [],
    groupControlList: [],
    scenarioList: [],
    scenarioTriggerList: [],
    bridgeDeviceList: [],
    bridgeDeviceTypeList: [],
    gatewayList: [],
    cameraList: [],
    cameraBrandList: [],
    inputList: [],
    inputTypeList: [],
    sensorTypeList: [],
    areaList: [],
    mediaList: [],
    mediaInputList: [],
    virtualControlList: [],
    mediaStatus: 'closed',
    intercomState: {
        status: 'ended',
        info: null,
        is_incoming_call: true,
        id: '',
        name: '',
    },
    bellStatus: 'ended',
    doorList: [],
    doorBrandList: [],
    intercomList: [],
    alarmState: {
        default: {
            level: 0,
            ringing: false,
            will_ring: false,
            latest_log_list: [],
        },
    },
    sound: {
        intercom_level: 0,
        incoming_call_flat_front_level: 0,
        incoming_call_outside_panel_level: 0,
        alarm_level: 0,
        microphone_level: 0,
        click_sound: false,
    },
    config: {
        alarm: {
            first_level_alarm_countdown_duration: 0,
            second_level_alarm_countdown_duration: 0,
            third_level_alarm_countdown_duration: 0,
            reactivation_duration_after_reset: 0,
            siren_interval_on_duration: 0,
            siren_interval_off_duration: 0,
            siren_interval_repeat_count: 0,
        },
        alarm_settings: {
            home_timer_seconds: 0,
            outside_timer_seconds: 0,
        },
        location: null,
        language: 'tr',
    },
    homepageItemList: [],
    locationList: [],
    menuList: [],
    stpUIList: [],
    languageList: [],
    conciergeList: [],
    conciergeTypeList: [],
    screenSaverStatus: "",
};

export type IntercomState = {
    status: string; // todo enum
    info: IntercomModel;
    is_incoming_call: boolean;
    id: string;
    name: string;
};

export type AlarmLogModel = {
    timestamp: number;
    log_message: string;
    sensor_type_id: string;
};

export type AlarmStateModel = {
    level: number, // todo enum
    ringing: boolean,
    will_ring: boolean,
    latest_log_list: AlarmLogModel[]
};

export type AlarmState = {
    default: AlarmStateModel;
};

export type ScenarioOperationModel = {
    type: string;
    relational_id: string;

    value: number;
    delay_seconds: number;
};

export type ScenarioModel = {
    id: string;
    name: string;
    operation_list: ScenarioOperationModel[];
    is_playing?: boolean;
    show_on_footer?: boolean;
    is_notification?: boolean;
    ifttt_trigger_name?: string;
};


export type ScenarioTriggerModel = {
    id: string;
    name: string;
    scenario_id: string;
    settings: TimeTriggerSettingsModel;
};

export type TimeTriggerType = 'exact' | 'sunrise' | 'sunset';

export type TimeTriggerSettingsModel = {
    day_list: number[];
    type: TimeTriggerType;
    delay_in_seconds: number;
};

export type CreateMediaDeviceModel = {
    name: string;
    type: string;
    bridge_device_id: string;
};

export type AreaModel = {
    id: string;
    name: string;
    type: 'place' | 'floor' | 'room';
    parent_area_id: string;
};
export type AlarmSettingsModel = {
    home_timer_seconds: number;
    outside_timer_seconds: number;
};

export type ConfigModel = {
    id?: never,
    alarm: AlarmConfigModel;
    alarm_settings: AlarmSettingsModel;
    location: LocationModel;
    language: string;
};

export type MediaInputModel = {
    id: string;
    slot: number;
    name: string;
    bridge_device_id: string;
    control_id: string;
    value: number;
};

export type VirtualControlModel = {
    id: string;
    slot: number;
    bridge_device_id: string;
    control_id: string;
    registered_by_system?: boolean;
    parameters: Omit<GlassControlParameters, 'output_number'>;
};

export type HomepageItemType = 'control' | 'scenario' | 'sensor' | 'media';

export type HomepageItemModel = {
    id: string;
    type: HomepageItemType;
    relational_id: string;
    order: number;
};

export type SoundState = {
    intercom_level: number;
    incoming_call_flat_front_level: number;
    incoming_call_outside_panel_level: number;
    alarm_level: number;
    microphone_level: number;
    click_sound:boolean;
};

export type UpdateFCMPushTokenRequest = {
    os: string;
    token: string;
}

export type MenuContentType = 'control' | 'media';

export type MenuContent = {
    type: MenuContentType;
    relational_id: string;
    order: number;
}

export type MenuEntity = {
    id?: string;
    name: string;
    icon: string;
    order: number;
    content_list: MenuContent[];
    is_special: boolean;

    onClick?: (e?: any) => void;
}

export type STPUIType = 'group' | 'control' | 'virtualControl' | 'weather' | 'scenario' | 'media' | 'air';

export type STPUIEntity = {
    id: string;
    type: STPUIType;
    display_name: string;
    bridge_device_id: string;
    relational_id?: string;
    page_number: number;
    row_number: number;
}

export type ControlState = {
    controlTypeList: ControlTypeModel<unknown>[];
    userList: UserModel[];
    controlList: ControlModel[];
    groupControlList: ControlModel[];
    bridgeDeviceList: BridgeDeviceModel[];
    bridgeDeviceTypeList: BridgeDeviceTypeModel[];
    gatewayList: GatewayModel[];
    scenarioList: ScenarioModel[];
    scenarioTriggerList: ScenarioTriggerModel[];
    cameraList: CameraModel[];
    cameraBrandList: CameraBrandModel[];
    conciergeList: ConciergeModel[];
    conciergeTypeList: ConciergeTypeModel[];
    doorList: DoorModel[];
    doorBrandList: DoorBrandModel[];
    intercomList: IntercomModel[];
    inputList: InputModel[];
    inputTypeList: InputTypeModel[];
    sensorTypeList: SensorTypeModel[];
    areaList: AreaModel[];
    mediaList: MediaModel[];
    mediaInputList: MediaInputModel[];
    virtualControlList: VirtualControlModel[];
    homepageItemList: HomepageItemModel[];
    locationList: LocationModel[];
    mediaStatus: string; // todo enum
    intercomState: IntercomState;
    bellStatus: string; // todo enum
    alarmState: AlarmState;
    sound: SoundState;
    config: ConfigModel;
    menuList: MenuEntity[];
    stpUIList: STPUIEntity[];
    languageList: LanguageModel[];
    screenSaverStatus: string;
};

export type LanguageModel = {
    id: string;
    name: string;
    variables: Map<string, string>;
}
